import { useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Slider, Typography } from '@mui/material';
import { BaseModal } from '@/components/ui/BaseModal';
import { calibrationOffset } from '@/utils/arcade/musicAnalysis';
import type { ArcadePrefs } from '@/utils/arcade/scores';
import { arcadeTelemetry } from '@/utils/arcade/telemetry';

export function ArcadeControls({
  open,
  close,
  prefs,
  change,
}: {
  open: boolean;
  close: () => void;
  prefs: ArcadePrefs;
  change: (next: Partial<ArcadePrefs>) => void;
}) {
  const [running, setRunning] = useState(false);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState(
    'Use the headphones or speakers you play with. Tap along with eight clicks.'
  );
  const session = useRef<{
    context: AudioContext;
    start: number;
    taps: number[];
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const stop = () => {
    if (session.current) {
      clearTimeout(session.current.timer);
      void session.current.context.close();
      session.current = null;
    }
    setRunning(false);
  };
  useEffect(() => {
    if (!open) stop();
    return () => {
      if (session.current) {
        clearTimeout(session.current.timer);
        void session.current.context.close();
        session.current = null;
      }
    };
  }, [open]);
  const begin = async () => {
    stop();
    try {
      const context = new AudioContext();
      await context.resume();
      const base = context.currentTime + 0.8;
      const start = performance.now() + (base - context.currentTime) * 1000;
      for (let i = 0; i < 24; i++) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = i % 4 === 0 ? 1000 : 700;
        oscillator.connect(gain);
        gain.connect(context.destination);
        gain.gain.setValueAtTime(0.15, base + i * 0.5);
        gain.gain.exponentialRampToValueAtTime(0.001, base + i * 0.5 + 0.045);
        oscillator.start(base + i * 0.5);
        oscillator.stop(base + i * 0.5 + 0.05);
      }
      session.current = {
        context,
        start,
        taps: [],
        timer: setTimeout(() => {
          stop();
          setMessage('Calibration timed out. Try again when you can hear the clicks.');
        }, 13000),
      };
      setCount(0);
      setRunning(true);
      setMessage('Tap in time with the clicks.');
    } catch {
      setMessage('Audio could not start. Use the timing slider or try again.');
    }
  };
  const tap = () => {
    const s = session.current;
    if (!s || performance.now() < s.start) return;
    const now = performance.now();
    if (now - (s.taps.at(-1) ?? 0) < 250) return;
    s.taps.push(now);
    setCount(s.taps.length);
    if (s.taps.length >= 8) {
      const offset = calibrationOffset(s.taps, s.start);
      stop();
      if (offset === null) setMessage('Those taps varied too much. Try again at a steady pace.');
      else {
        change({ timingOffsetMs: offset });
        setMessage(`Saved ${offset} ms. Rhythm games now allow for your timing.`);
        arcadeTelemetry('calibration', 'settings', '', { offsetMs: offset });
      }
    }
  };
  return (
    <BaseModal
      isOpen={open}
      onClose={close}
      desktopModalMobileDrawer
      paperSx={{ backgroundColor: '#111721', color: '#f5f7ff' }}
      bodySx={{ backgroundColor: '#111721', color: '#f5f7ff' }}
    >
      <Box
        sx={{
          p: '18px',
          display: 'grid',
          gap: '16px',
          maxWidth: 480,
          fontSize: '15px',
          '& .MuiTypography-root': { fontSize: '15px' },
          '& .MuiButton-root': { minHeight: 48, fontSize: '14px' },
          '& .MuiCheckbox-root': { padding: '10px', color: '#b7c5dc', '&.Mui-checked': { color: '#79baff' } },
          '& .MuiButton-text': { color: '#a4ceff' },
          '& .MuiSvgIcon-root': { fontSize: '24px' },
        }}
      >
        <Typography component="h2" sx={{ fontWeight: 800 }}>
          Timing and touch
        </Typography>
        <Typography>{message}</Typography>
        {running ? (
          <Button
            variant="contained"
            onPointerDown={(e) => {
              e.preventDefault();
              tap();
            }}
            onKeyDown={(e) => {
              if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
                e.preventDefault();
                tap();
              }
            }}
            aria-label="Tap along"
          >
            Tap along · {count}/8
          </Button>
        ) : (
          <Button variant="contained" onClick={() => void begin()}>
            Calibrate timing
          </Button>
        )}
        <label>
          Timing offset: {prefs.timingOffsetMs} ms
          <Slider
            aria-label="Timing offset"
            min={-200}
            max={200}
            step={5}
            value={prefs.timingOffsetMs}
            onChange={(_, v) => change({ timingOffsetMs: v as number })}
          />
        </label>
        <Typography>
          Positive values compensate for taps that arrive late. Timing changes apply to Beat Lanes
          and Pulse Pads.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={prefs.touchMode === 'relative'}
              onChange={(_, checked) => change({ touchMode: checked ? 'relative' : 'direct' })}
            />
          }
          label="Thumb-relative steering"
        />
        <Typography>
          In Bass Blaster and Night Drive, drag from anywhere without snapping to your finger.
        </Typography>
        <label>
          Steering sensitivity: {prefs.sensitivity.toFixed(1)}×
          <Slider
            aria-label="Steering sensitivity"
            min={0.5}
            max={2}
            step={0.1}
            value={prefs.sensitivity}
            onChange={(_, v) => change({ sensitivity: v as number })}
          />
        </label>
        <FormControlLabel
          control={
            <Checkbox
              checked={prefs.largeTargets}
              onChange={(_, checked) => change({ largeTargets: checked })}
            />
          }
          label="Larger pads and game buttons"
        />
        <Button onClick={close}>Done</Button>
      </Box>
    </BaseModal>
  );
}
